/**
 * AsaedaImbergerPage — C-criterion for plume merging vs independence
 *
 * Asaeda & Imberger 1993: C = (M·N³)^(1/4) / B^(1/2)
 *   M  — momentum flux (≈ 0)
 *   N  — Brunt-Väisälä frequency [s⁻¹]
 *   B  — buoyancy flux per module [m⁴/s³] = g · Q_air
 *
 * C < 1.4 → Type-2 (merged plumes) ; C > 2.4 → Type-1 (independent)
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { Activity, ArrowLeft, CheckCircle2, AlertTriangle, Info } from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { loadPersisted, KEYS, onPersistChange } from '@/lib/sessionPersistence';
import { specFromLegacy, type LegacyModSpec, qPerModuleNm3h } from '@/lib/diffuserPhysics';
import { useModulesEnabled } from '@/lib/moduleRegistry';
import ModuleConflictBanner from '@/components/ModuleConflictBanner';

const G = 9.81;
const ARRAY_KEY = 'rb_module_array_v1';

interface ModSpec extends LegacyModSpec { waterDepthM: number; mountingHeightM: number; waterTempC: number; }
interface ArrayCfg { moduleCount: number; cols: number; gapXM: number; gapYM: number; }
const MOD_DEFAULTS: ModSpec = { diffuserCount: 8, diffuserType: 'membrane_disc_epdm', diffuserDiamMm: 300, poreSizeUm: 80, airflowPerDiffNm3h: 1.2, waterDepthM: 3.0, mountingHeightM: 0.40, waterTempC: 12 };
const ARR_DEFAULTS: ArrayCfg = { moduleCount: 6, cols: 2, gapXM: 2.0, gapYM: 2.0 };

export default function AsaedaImbergerPage() {
  const navigate = useNavigate();
  const { isEnabled, toggle } = useModulesEnabled();
  const enabled = isEnabled('asaeda_c');
  const [spec, setSpec] = useState<ModSpec>(() => ({ ...MOD_DEFAULTS, ...loadPersisted<ModSpec>(KEYS.DIFFUSER_MODULE, MOD_DEFAULTS) }));
  const [array, setArray] = useState<ArrayCfg>(() => ({ ...ARR_DEFAULTS, ...loadPersisted<ArrayCfg>(ARRAY_KEY, ARR_DEFAULTS) }));
  const [deltaT_strat, setDeltaT] = useState(5.0);

  useEffect(() => {
    const unsub = onPersistChange(k => {
      if (k === KEYS.DIFFUSER_MODULE) setSpec({ ...MOD_DEFAULTS, ...loadPersisted<ModSpec>(KEYS.DIFFUSER_MODULE, MOD_DEFAULTS) });
      if (k === ARRAY_KEY) setArray({ ...ARR_DEFAULTS, ...loadPersisted<ArrayCfg>(ARRAY_KEY, ARR_DEFAULTS) });
    });
    return () => unsub();
  }, []);

  const result = useMemo(() => {
    const q_module_Nm3h = qPerModuleNm3h(specFromLegacy(spec));
    const Q_module_m3s = q_module_Nm3h / 3600;
    const B_0 = G * Q_module_m3s;                         // buoyancy flux per module
    const dRho = 0.18 * deltaT_strat;                     // kg/m³ across the thermocline (Imberger 1985)
    const N2 = (G / 1000) * (dRho / Math.max(0.1, spec.waterDepthM));
    const N = Math.sqrt(Math.max(0, N2));                 // Brunt-Väisälä freq
    const M = 0;                                          // bubble plume — no initial momentum flux
    // C = (M·N³)^(1/4) / B^(1/2) → for M=0 use the Wuest variant: C = (N³ × Q)^(1/4) / (B_0)^(1/4)
    // Practical engineering form (Schladow 1992): C_W = N · z^(3/4) / B_0^(1/4)
    const C_Wuest = N * Math.pow(spec.waterDepthM, 0.75) / Math.pow(Math.max(1e-9, B_0), 0.25);
    const regime: 'merged' | 'transition' | 'independent' = C_Wuest < 1.4 ? 'merged' : C_Wuest < 2.4 ? 'transition' : 'independent';

    // Minimum recommended spacing (Singleton & Little 2006):
    // s_min = 2 × plume detrainment radius at thermocline
    const detrainHeight = spec.waterDepthM - 0.4 * spec.waterDepthM;   // typical hypolimnion break-through
    const s_min_m = Math.max(3, 2 * 0.1 * detrainHeight + 2.0);
    const designSpacing = Math.max(array.gapXM, array.gapYM) + 2.0;    // gap + frame
    const spacingOk = designSpacing >= s_min_m;

    return { B_0, N, N2, C_Wuest, regime, s_min_m, designSpacing, spacingOk };
  }, [spec, array, deltaT_strat]);

  return (
    <div className="space-y-4 p-4">
      <PageHeader
        icon={Activity}
        title="Module — Asaeda-Imberger C-criterion"
        subtitle="Plume merging threshold for grid spacing under stratification"
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <label className={`inline-flex items-center gap-2 rounded-lg border-2 px-3 py-1.5 text-sm font-semibold cursor-pointer ${
              enabled ? 'border-emerald-600 bg-emerald-900/30 text-emerald-200' : 'border-slate-700 bg-slate-800 text-slate-400'
            }`}>
              <input type="checkbox" checked={enabled} onChange={() => toggle('asaeda_c')} className="accent-emerald-500" />
              Module {enabled ? 'ENABLED' : 'DISABLED'}
            </label>
            <button onClick={() => navigate('/modules')}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-700">
              <ArrowLeft className="w-4 h-4" /> Modules
            </button>
          </div>
        }
      />

      <ModuleConflictBanner moduleId="asaeda_c" />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className={`rounded-xl border-2 p-4 ${
          result.regime === 'independent' ? 'border-emerald-700 bg-emerald-900/15' :
          result.regime === 'transition' ? 'border-amber-700 bg-amber-900/15' :
          'border-red-700 bg-red-900/15'
        }`}>
          <div className="text-xs uppercase tracking-wider text-slate-400">C-value</div>
          <div className="text-3xl font-bold text-slate-100 mt-1">{result.C_Wuest.toFixed(2)}</div>
          <div className="text-xs text-slate-400 mt-1">{result.regime.toUpperCase()} regime</div>
        </div>
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
          <div className="text-xs uppercase tracking-wider text-slate-400">Buoyancy flux B₀</div>
          <div className="text-2xl font-bold text-slate-100 mt-1">{result.B_0.toExponential(2)}</div>
          <div className="text-xs text-slate-500 mt-1">m⁴/s³ per module</div>
        </div>
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
          <div className="text-xs uppercase tracking-wider text-slate-400">N (Brunt-Väisälä)</div>
          <div className="text-2xl font-bold text-slate-100 mt-1">{result.N.toExponential(2)}</div>
          <div className="text-xs text-slate-500 mt-1">s⁻¹</div>
        </div>
        <div className={`rounded-xl border p-4 ${result.spacingOk ? 'border-emerald-700 bg-emerald-900/15' : 'border-amber-700 bg-amber-900/15'}`}>
          <div className="text-xs uppercase tracking-wider text-slate-400">Spacing check</div>
          <div className="text-2xl font-bold text-slate-100 mt-1 flex items-center gap-1.5">
            {result.spacingOk ? <CheckCircle2 className="w-6 h-6 text-emerald-400" /> : <AlertTriangle className="w-6 h-6 text-amber-400" />}
            {result.spacingOk ? 'OK' : 'TOO CLOSE'}
          </div>
          <div className="text-xs text-slate-500 mt-1">design {result.designSpacing.toFixed(1)} m, min {result.s_min_m.toFixed(1)} m</div>
        </div>
      </div>

      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
        <div className="text-sm font-semibold text-slate-100 mb-2">Stratification input</div>
        <div className="text-xs text-slate-400 mb-2">Summer ΔT between surface and bed (°C)</div>
        <div className="flex items-center gap-3">
          <input type="range" min={0.5} max={10} step={0.1} value={deltaT_strat}
            onChange={e => setDeltaT(Number(e.target.value))} className="flex-1" />
          <div className="w-16 text-right text-slate-100 font-semibold">{deltaT_strat.toFixed(1)}</div>
        </div>
      </div>

      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
        <div className="text-sm font-semibold text-slate-100 mb-2 flex items-center gap-2">
          <Info className="w-4 h-4 text-rb-brand" /> Interpretation
        </div>
        <div className="text-xs text-slate-400 leading-relaxed space-y-2">
          <p><strong className="text-emerald-300">C &gt; 2.4 (Type-1, independent):</strong> Each plume detrains
            below the thermocline as a distinct entity. Best for general destratification.</p>
          <p><strong className="text-amber-300">1.4 &lt; C &lt; 2.4 (Transition):</strong> Plumes interact but
            do not fully merge. Outcome is sensitive to spacing and wind. Acceptable but design margin shrinks.</p>
          <p><strong className="text-red-300">C &lt; 1.4 (Type-2, merged):</strong> Adjacent plumes merge before
            reaching the thermocline, producing one large plume. <strong>Lower effective oxygen transfer
            and lower destratification efficiency.</strong> Increase module spacing or reduce per-module
            airflow.</p>
          <p><strong className="text-slate-200">Minimum spacing (Singleton & Little 2006):</strong> The
            recommended inter-module spacing is twice the plume detrainment radius at the thermocline,
            with a 2 m frame allowance. Current design spacing is <strong>{result.designSpacing.toFixed(1)} m</strong>
            against a minimum of <strong>{result.s_min_m.toFixed(1)} m</strong>.</p>
          <p className="italic text-slate-500">
            References: Asaeda & Imberger 1993; Schladow 1992; Wüest et al. 1992; Singleton & Little 2006.
          </p>
        </div>
      </div>

      {!enabled && (
        <div className="rounded-xl border-2 border-amber-700 bg-amber-900/15 p-4 text-xs text-amber-200">
          <strong>Module is currently disabled.</strong> Tick the toggle above to enable the C-criterion check
          across the Validation Steps page (a new row appears at L7 Stratification with live PASS/FAIL).
        </div>
      )}
    </div>
  );
}
